// Admin survey authoring RPCs. FORKED — admin-only, never imported by employee surface.
// Trust-model invariant: all reads/writes funnel through SECURITY DEFINER RPCs gated
// on pulse_is_admin(). Legacy participant tables are NEVER touched from this module.
import { supabase } from '@/lib/supabase';

export type AdminSurveyRow = {
  id: string;
  name: string;
  description: string | null;
  status: string;
  north_vision: string | null;
  created_at: string;
  updated_at: string;
  is_active: boolean;
};

export type AdminAnchor = {
  id?: string;
  name: string;
  description?: string | null;
  scale_type?: 'likert_5' | 'binary';
  scale_labels?: Record<string, string>;
  north_vision_target?: number | null;
  sort_order?: number;
  is_active?: boolean;
  interview_guidance?: unknown;
};

export type AdminSurveyDetail = {
  survey: {
    id: string;
    name: string;
    description: string | null;
    status: string;
    north_vision: string | null;
    pulse_org_id: string | null;
    created_at: string;
    updated_at: string;
  };
  anchors: Array<AdminAnchor & { id: string; survey_id: string }>;
};

export async function listSurveys(pulseOrgId: string): Promise<AdminSurveyRow[]> {
  const { data, error } = await supabase.rpc('admin_list_surveys', { _pulse_org_id: pulseOrgId });
  if (error) throw error;
  return (data ?? []) as AdminSurveyRow[];
}

export async function getSurveyWithAnchors(surveyId: string): Promise<AdminSurveyDetail> {
  const { data, error } = await supabase.rpc('admin_get_survey_with_anchors', { _survey_id: surveyId });
  if (error) throw error;
  return data as unknown as AdminSurveyDetail;
}

export async function upsertSurvey(args: {
  pulseOrgId: string;
  surveyId?: string | null;
  name: string;
  description?: string | null;
  northVision?: string | null;
}): Promise<string> {
  const { data, error } = await supabase.rpc('admin_upsert_survey', {
    _pulse_org_id: args.pulseOrgId,
    _survey_id: args.surveyId ?? null,
    _name: args.name,
    _description: args.description ?? null,
    _north_vision: args.northVision ?? null,
  });
  if (error) throw error;
  return data as unknown as string;
}

export async function upsertAnchors(surveyId: string, anchors: AdminAnchor[]): Promise<string[]> {
  const { data, error } = await supabase.rpc('admin_upsert_anchors', {
    _survey_id: surveyId,
    _anchors: anchors as unknown as never,
  });
  if (error) throw error;
  const out = (data as { upserted_ids?: string[] } | null)?.upserted_ids ?? [];
  return out;
}

export async function setActiveSurvey(pulseOrgId: string, surveyId: string | null): Promise<void> {
  const { error } = await supabase.rpc('set_active_survey', {
    _pulse_org_id: pulseOrgId,
    _survey_id: surveyId,
  });
  if (error) throw error;
}

export type SurveyDataCounts = {
  anchors: number;
  anchor_responses: number;
  is_active: boolean;
};

export async function getSurveyDataCounts(surveyId: string): Promise<SurveyDataCounts> {
  const { data, error } = await supabase.rpc('admin_survey_data_counts', { _survey_id: surveyId });
  if (error) throw error;
  return data as unknown as SurveyDataCounts;
}

export async function setSurveyStatus(
  surveyId: string,
  status: 'draft' | 'active' | 'paused' | 'closed' | 'archived',
): Promise<void> {
  const { error } = await supabase.rpc('admin_set_survey_status', {
    _survey_id: surveyId,
    _status: status,
  });
  if (error) throw error;
}

export async function deleteSurvey(surveyId: string): Promise<void> {
  const { error } = await supabase.rpc('admin_delete_survey', { _survey_id: surveyId });
  if (error) throw error;
}

export type SurveyReadiness = {
  has_name: boolean;
  has_vision: boolean;
  anchor_count: number;
  missing_guidance_count: number;
  missing_target_count: number;
  ready: boolean;
};

export async function getSurveyReadiness(surveyId: string): Promise<SurveyReadiness> {
  const { data, error } = await supabase.rpc('admin_survey_readiness', { _survey_id: surveyId });
  if (error) throw error;
  return data as unknown as SurveyReadiness;
}

// Calls the generate-anchors edge fn. Caller must be admin (edge guard enforces).
export async function suggestAnchors(args: {
  surveyId: string;
  northVision: string;
  currentState?: string;
}): Promise<AdminAnchor[]> {
  const { data, error } = await supabase.functions.invoke('generate-anchors', {
    body: {
      survey_id: args.surveyId,
      north_vision: args.northVision,
      current_state: args.currentState ?? null,
    },
  });
  if (error) throw error;
  const anchors = (data as { anchors?: AdminAnchor[] } | null)?.anchors ?? [];
  return anchors;
}

// Generates interview_guidance for all anchors missing it (or all, if regenerate).
// Edge fn writes guidance directly to anchors via service role after admin gate.
export async function generateInterviewFrameworks(args: {
  surveyId: string;
  regenerate?: boolean;
}): Promise<{ count: number; attempted: number; failed: number; warning: string | null }> {
  const { data, error } = await supabase.functions.invoke('generate-interview-framework', {
    body: { survey_id: args.surveyId, regenerate: !!args.regenerate },
  });
  if (error) throw error;
  const d = (data as {
    results?: unknown[];
    attempted?: number;
    failed_count?: number;
    time_budget_warning?: string | null;
  }) ?? {};
  const count = d.results?.length ?? 0;
  const attempted = d.attempted ?? count;
  return { count, attempted, failed: d.failed_count ?? Math.max(0, attempted - count), warning: d.time_budget_warning ?? null };
}

// --- Reports & analytics (admin-gated, k>=5 floor enforced server-side) ---

export type ReportAnchorRow = {
  anchor_id: string;
  name: string;
  description: string | null;
  sort_order: number;
  north_vision_target: number | null;
  n: number;
  suppressed: boolean;
  mean_score: number | null;
  std_dev: number | null;
  gap_from_target: number | null;
};

export type SurveyReport = {
  survey: { id: string; name: string; description: string | null; north_vision: string | null; status: string };
  anchors: ReportAnchorRow[];
  generated_at: string;
};

export async function getSurveyReport(surveyId: string): Promise<SurveyReport> {
  const { data, error } = await supabase.rpc('admin_get_survey_report', { _survey_id: surveyId });
  if (error) throw error;
  return data as unknown as SurveyReport;
}

export type AnalyticsRadarPoint = {
  anchor_id: string; name: string; target: number | null; value: number | null; n: number; suppressed: boolean;
};

export type AnalyticsTension = {
  anchor_id: string; name: string; gap: number; n: number;
};

export type SurveyAnalytics = {
  radar: AnalyticsRadarPoint[];
  tensions: AnalyticsTension[];
  generated_at: string;
};

export async function getSurveyAnalytics(surveyId: string): Promise<SurveyAnalytics> {
  const { data, error } = await supabase.rpc('admin_get_survey_analytics', { _survey_id: surveyId });
  if (error) throw error;
  return data as unknown as SurveyAnalytics;
}

// --- AI-generated leader insights (confidential, k>=5 floor) ---

export type LeaderInsightAnchor = {
  anchor_id: string;
  name: string;
  themes: string[];
  suggested_actions: string[];
  reflection_prompts: string[];
};

export type LeaderInsights = {
  generated_at: string;
  k_floor_met: boolean;
  distinct_employees: number;
  systemic_insight: string | null;
  priorities: string[];
  per_anchor: LeaderInsightAnchor[];
  survey?: { id: string; name: string; north_vision: string | null };
  anchors_visible?: number;
  tensions_count?: number;
  note?: string;
  cached?: boolean;
  stale?: boolean;
  current_fingerprint?: string;
};

export type LeaderInsightsPeekEmpty = { cached: false; stale: false; payload: null };
export type LeaderInsightsResponse = LeaderInsights | LeaderInsightsPeekEmpty;

export function isLeaderInsightsEmpty(
  r: LeaderInsightsResponse,
): r is LeaderInsightsPeekEmpty {
  return (r as LeaderInsightsPeekEmpty).payload === null;
}

export async function getLeaderInsights(
  surveyId: string,
  opts: { force?: boolean; peek?: boolean } = {},
): Promise<LeaderInsightsResponse> {
  const { data, error } = await supabase.functions.invoke('survey-leader-insights', {
    body: { survey_id: surveyId, force: !!opts.force, peek: !!opts.peek },
  });
  if (error) throw error;
  return data as LeaderInsightsResponse;
}
